import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {selectUserData, selectUserLoading} from '~/shared/store/storeModules/user';
import {getLocalizationService} from '~/shared/services/localisationService';
import {body14Bold, body14Normal, body20Bold, subheader24Bold} from '~/shared/theme/typography';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';
import {flexCenterVertically, flexColumn} from '~/shared/theme/FlexLayout';
import TenbisCredit from '~/assets/images/icons/tenbisCredit.svg?react';
import {PriceLabel} from '~/shared/components/PriceLabels';
import {Skeleton, SkeletonLoader} from '~/shared/components/Loaders';
import CreditLoadingFailed from '~/shared/components/CreditLoadingFailed';

import UserCircle from '../../assets/userCircle.svg?react';
import {usePersonalAreaContext} from '../../context/PersonalAreaContext';

const Root = styled.div`
  ${flexColumn}
  padding: 40px;
`;

const HeaderWrapper = styled.div`
  ${flexCenterVertically}
  margin-bottom: 8px;
`;

const Header = styled.div`
  ${body20Bold}
  line-height: 28px;
  ${flipOnLTR`
    margin-right: 8px;
  `}
`;

const UserInfo = styled.div`
  ${flexColumn}
`;

const InfoRow = styled.div`
  ${flexCenterVertically}
  ${body14Normal}
  padding: 16px 0;
  border-bottom: 1px solid ${({theme}) => theme.colors.blackOpacity08};
`;

const Label = styled.span`
  color: ${({theme}) => theme.colors.contentPlaceholder};
  min-width: 33px;
  ${flipOnLTR`
    margin-left: 20px;
  `}
`;

const Value = styled.span`
  color: ${({theme}) => theme.colors.charcoal80};
`;

const CreditSection = styled.div`
  display: flex;
  margin-top: 40px;
`;

const BalanceValue = styled(PriceLabel)`
  ${subheader24Bold}
  line-height: 32px;
`;

const StyledTenbisCredit = styled(TenbisCredit)`
  width: 26px;
  height: 26px;
  ${flipOnLTR`
    margin-left: 8px;
  `}

  path {
    fill: ${({theme}) => theme.colors.contentBrand};
  }
`;

const RowsWrapper = styled.div`
  ${flipOnLTR`
    padding-right: 32px;
  `}
`;

const BalanceWrapper = styled.div`
  ${flexColumn}
  color: ${({theme}) => theme.colors.charcoal80};
`;

const BalanceLabel = styled.div`
  ${body14Bold}
  line-height: 20px;
  margin-top: 4px;
`;

const ValueSkeleton = styled(Skeleton).attrs({
  width: 96,
  height: 14,
})`
  border-radius: 6px;
`;

const BalanceSkeleton = styled(ValueSkeleton).attrs({
  height: 24,
})``;

const PersonalArea = () => {
  const {t} = getLocalizationService();

  const userData = useSelector(selectUserData);
  const isUserLoading = useSelector(selectUserLoading);
  const {tenbisCreditBalance, isMoneyCardsLoaded, moneyCardsError, refetchMoneyCards} = usePersonalAreaContext();
  
  return (
    <Root>
      <HeaderWrapper>
        <UserCircle />
        <Header>{t('user_info_2')}</Header>
      </HeaderWrapper>
      
      <UserInfo>
        <RowsWrapper>
          <InfoRow>
            <Label>{t('name')}</Label>
            <SkeletonLoader shouldShowLoader={isUserLoading} LoaderComponent={ValueSkeleton}>
              <Value data-test-id="userName">{`${userData?.firstName} ${userData?.lastName}`}</Value>
            </SkeletonLoader>
          </InfoRow>

          <InfoRow>
            <Label>{t('email_2')}</Label>
            <SkeletonLoader shouldShowLoader={isUserLoading} LoaderComponent={ValueSkeleton}>
              <Value data-test-id="userEmail">{userData?.email}</Value>
            </SkeletonLoader>
          </InfoRow>

          <InfoRow>
            <Label>{t('phone')}</Label>
            <SkeletonLoader shouldShowLoader={isUserLoading} LoaderComponent={ValueSkeleton}>
              <Value data-test-id="userPhone">{userData?.cellphone}</Value>
            </SkeletonLoader>
          </InfoRow>
        </RowsWrapper>
        <CreditSection>
          <StyledTenbisCredit viewBox="0 0 24 24"/>
          {moneyCardsError ? (
            <CreditLoadingFailed onRetry={refetchMoneyCards} />
          ) : (
            <BalanceWrapper>
              <SkeletonLoader shouldShowLoader={!isMoneyCardsLoaded} LoaderComponent={BalanceSkeleton}>
                <BalanceValue priceDataTestId="userCreditBalance" price={tenbisCreditBalance} />
              </SkeletonLoader>
              <BalanceLabel>{t('credit_balance')}</BalanceLabel>
            </BalanceWrapper>
          )}
        </CreditSection>
      </UserInfo>
    </Root>
  );
};

export default PersonalArea;
